/**
 * WorkspaceRedirect - Redirects legacy workspace URLs to the canonical workspace path.
 *
 * Redirects:
 * - /workspace/* → /w/*
 * - /clients/:slug/* → /w/:slug/*
 *
 * Preserves deep paths for backwards compatibility with bookmarks and links.
 */

import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useClient } from "@/context/ClientContext";
import { getClientSlug } from "@/types/client";

export function WorkspaceRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const clientSlugFromUrl = params.clientSlug;
  const splat = (params["*"] ?? "").trim(); // Deep path after slug route
  const { currentClient } = useClient();

  // Canonical prefix is always /w
  const canonicalPrefix = "/w";

  useEffect(() => {
    // Log for telemetry during transition (dev only)
    if (import.meta.env.DEV) {
      console.warn(
        `[WorkspaceRedirect] Legacy route accessed: ${location.pathname} -> redirecting to ${canonicalPrefix}`,
      );
    }

    // Resolve the client slug to use
    const resolvedSlug =
      clientSlugFromUrl ||
      (currentClient ? getClientSlug(currentClient) : null);

    if (!resolvedSlug) {
      // No client context, go to client list
      void navigate("/clients", { replace: true });
      return;
    }

    // Build the target path, preserving deep path and query string if present
    const basePath = splat
      ? `${canonicalPrefix}/${resolvedSlug}/${splat}`
      : `${canonicalPrefix}/${resolvedSlug}`;
    const target = `${basePath}${location.search}`;

    // Avoid redirect loops if already correct
    if (location.pathname + location.search !== target) {
      void navigate(target, { replace: true });
    }
  }, [
    clientSlugFromUrl,
    splat,
    currentClient,
    location.pathname,
    location.search,
    navigate,
    canonicalPrefix,
  ]);

  // Show loading while redirecting
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
        <p className="text-sm text-[var(--text-muted)]">Redirecting...</p>
      </div>
    </div>
  );
}

export default WorkspaceRedirect;
